import type { Vec3 } from "../packages/openplans-core/src/types";

/** Add two Vec3s. */
export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

/** Subtract b from a. */
export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

/** Scale a Vec3 by a scalar. */
export function scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

/** Translate every point by `delta`. Returns a new array; does not mutate. */
export function translatePoints(points: Vec3[], delta: Vec3): Vec3[] {
  return points.map((p) => add(p, delta));
}

/** Rodrigues rotation: rotate `point` by `angleRad` around an arbitrary unit
 *  `axis` pivoted at `anchor`. Pure function — does not mutate inputs. */
export function rotatePoint(
  point: Vec3,
  angleRad: number,
  axis: Vec3,
  anchor: Vec3,
): Vec3 {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const [ax, ay, az] = normalize(axis);

  const [px, py, pz] = sub(point, anchor);

  const dot = ax * px + ay * py + az * pz;

  const rx = px * cos + (ay * pz - az * py) * sin + ax * dot * (1 - cos);
  const ry = py * cos + (az * px - ax * pz) * sin + ay * dot * (1 - cos);
  const rz = pz * cos + (ax * py - ay * px) * sin + az * dot * (1 - cos);

  return [rx + anchor[0], ry + anchor[1], rz + anchor[2]];
}

/** Rotate every point. Returns a new array. */
export function rotatePoints(
  points: Vec3[],
  angleRad: number,
  axis: Vec3,
  anchor: Vec3,
): Vec3[] {
  return points.map((p) => rotatePoint(p, angleRad, axis, anchor));
}

/** Centroid of a point cloud. Returns [0,0,0] for empty input. */
export function centroid(points: Vec3[]): Vec3 {
  if (points.length === 0) return [0, 0, 0];
  const sum: Vec3 = [0, 0, 0];
  for (const p of points) {
    sum[0] += p[0];
    sum[1] += p[1];
    sum[2] += p[2];
  }
  const n = points.length;
  return [sum[0] / n, sum[1] / n, sum[2] / n];
}

/** Convert degrees to radians. */
export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Default rotation axis (Y-up, the typical "yaw" axis for AEC plans). */
export const Y_UP: Vec3 = [0, 1, 0];

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len === 0) return [0, 1, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}
